import { ProjectsListBox } from "@selector/shared/components";
import { atom, useAtom } from "jotai";
import { equals, is } from "ramda";
import { type FC, useEffect, useMemo, useState } from "react";
import {
  ActionButtonIntent,
  Callout,
  CalloutIntent,
  Card,
  DialogComponent,
  type DialogProps,
  Icons,
  LoadingSpinner,
  Modal,
  PanelOrientation,
  PanelPosition,
  ResizableTabPanels,
  useResizableTabPanelsContext,
  VStack,
} from "widgets";
import { useOverlay } from "widgets/hooks";
import { type FileDefinitionType } from "widgets/utils";

import { FileSearchProvider } from "@/modules/files/components/FileBrowserSearchProvider.tsx";
import { FileSearchFilters } from "@/modules/files/components/FileSearchFilters.tsx";
import { FileSearchResults } from "@/modules/files/components/FileSearchResults.tsx";
import type { BrowsingProjectFileFragment } from "@/modules/files/graphql/files-queries.gql.ts";
import { type FileSuggestionsContextProps, useFileSearchStore } from "@/modules/files/stores/file-search.ts";
import { isFileOfType } from "@/modules/files/utils/file.ts";
import { getProjectFileIdFromFolder } from "@/modules/files/utils/utils.ts";
import { getProjectLabel } from "@/redux/modules/config.ts";
import { FILE_BROWSE_TYPES } from "@/settings/settings.ts";

import { useFilePickerStore } from "../stores/file-browser.ts";
import { FileBrowser, type FileBrowserProps } from "./FileBrowser.tsx";

export interface FilePickerProps extends Pick<DialogProps, "title" | "mainButton"> {
  isSelectableFilter?: FileBrowserProps["isSelectableFilter"];
  baseFilterHint?: string;
  selectableFileTypes?: FILE_BROWSE_TYPES[];
  singleSelection?: boolean;
  onValid: (files: BrowsingProjectFileFragment[]) => Promise<void>;
  fileDefinitionTypes?: FileDefinitionType[] | string[] | null;
  alreadySelectedFiles?: number[] | null;
  getFileSuggestionsContext?: () => Promise<FileSuggestionsContextProps>;
  /** @default "projects" */
  defaultTab?: "projects" | "filters";
  isOpen: boolean;
  onClose: () => void;
}

const selectedProjectAtom = atom<number | null | undefined>(undefined);

export const FilePickerControlledDialog: FC<FilePickerProps> = (props) => {
  return (
    <FileSearchProvider>
      <FilePickerControlledDialogComponent {...props} />
    </FileSearchProvider>
  );
};

const FilePickerControlledDialogComponent: FC<FilePickerProps> = (props) => {
  const filePickerSelectedFiles = useFilePickerStore((state) => state.selectedFiles);
  const filePickerError = useFilePickerStore((state) => state.error);
  const fileSearchSelectedFiles = useFileSearchStore((state) => state.selectedFiles);
  const fileSearchError = useFileSearchStore((state) => state.error);

  const { overlayProps, state } = useOverlay({
    isOpen: props.isOpen,
    onOpenChange(isOpen) {
      if (!isOpen) props.onClose();
    },
  });
  const [isLoadingContext, setLoadingContext] = useState<boolean>(true);
  const setContext = useFileSearchStore((state) => state.setContext);

  const [defaultTabId, setDefaultTabId] = useState<"projects" | "filters" | undefined>(props.defaultTab ?? "projects");

  // trigger the loading of the context from the parent perspective (e.g. genome viewer or Playbook...)
  // and choose the default open tab accordingly
  useEffect(() => {
    if (props.getFileSuggestionsContext) {
      void props.getFileSuggestionsContext().then((result) => {
        if (result.samples?.length || result.parents?.length || result.organisms?.length || result.events?.length) {
          setDefaultTabId("filters");
        }

        setContext(result);
        setLoadingContext(false);
      });
    } else {
      setLoadingContext(false);
    }
  }, []);

  const isSelectableFilter: ((file: BrowsingProjectFileFragment) => boolean) | undefined = useMemo(() => {
    if (typeof props.isSelectableFilter === "function") {
      return (file) =>
        !!props.isSelectableFilter?.(file) && !props.alreadySelectedFiles?.find((fileId) => fileId == file.id);
    }

    if (is(Array, props.fileDefinitionTypes) && props.fileDefinitionTypes.length > 0) {
      return (file) => {
        for (const filterType of props.fileDefinitionTypes!) {
          if (isFileOfType(filterType)(file)) {
            return true;
          }
        }

        return false;
      };
    }
  }, [props.alreadySelectedFiles, props.isSelectableFilter]);

  const hasFolderOnly = useMemo(
    () => equals<FILE_BROWSE_TYPES[] | undefined>([FILE_BROWSE_TYPES.DIRECTORY], props.selectableFileTypes),
    [props.selectableFileTypes],
  );

  return (
    <Modal state={state} {...overlayProps}>
      <DialogComponent
        closeFn={props.onClose}
        mainButton={{
          label: "Select",
          isDisabled: isLoadingContext,
          onPress: () => {
            const selectedFiles = filePickerSelectedFiles?.length ? filePickerSelectedFiles : fileSearchSelectedFiles;
            void props.onValid(selectedFiles).then(() => props.onClose());
          },
          intent: ActionButtonIntent.Primary,
        }}
        {...props}
        title={props.title ?? "Select Files"}
        errorCallout={
          filePickerError || fileSearchError
            ? { message: filePickerError?.message ?? fileSearchError?.message ?? "" }
            : undefined
        }
        fullscreen
        width={580}
        contentStyle={{ overflow: "hidden", display: "flex", padding: 0, height: "100%" }}
      >
        <VStack style={{ flex: "1 1 100%", overflow: "hidden" }}>
          {props.baseFilterHint && (
            <Callout intent={CalloutIntent.Info} style={{ margin: "5px 7px 0" }}>
              {props.baseFilterHint}
            </Callout>
          )}
          {isLoadingContext ? (
            <LoadingSpinner />
          ) : (
            <ResizableTabPanels
              fixedPanel={PanelPosition.Leading}
              orientation={PanelOrientation.Horizontal}
              defaultSize={320}
              minSize={230}
              maxSize={380}
              defaultTabId={defaultTabId}
              style={{ overflow: "hidden", borderTop: "1px solid #d8e4ed" }}
            >
              <ResizableTabPanels.FixedPanel showTabsLabel>
                <ResizableTabPanels.FixedPanelTab icon={<TabIcon />} id="projects" position={0} title="Projects">
                  <ProjectList />
                </ResizableTabPanels.FixedPanelTab>
                {!hasFolderOnly && (
                  <ResizableTabPanels.FixedPanelTab
                    icon={"quick_reference_all"}
                    id="filters"
                    position={1}
                    title="Search"
                  >
                    <FileSearchFilters fileDefinitionTypes={props.fileDefinitionTypes} />
                  </ResizableTabPanels.FixedPanelTab>
                )}
              </ResizableTabPanels.FixedPanel>

              <ResizableTabPanels.FlexPanel>
                <FilePickingPanel
                  singleSelection={props.singleSelection}
                  isSelectableFilter={isSelectableFilter}
                  selectableFileTypes={props.selectableFileTypes}
                />
              </ResizableTabPanels.FlexPanel>
            </ResizableTabPanels>
          )}
        </VStack>
      </DialogComponent>
    </Modal>
  );
};

const TabIcon: FC = () => {
  const { activeTabId } = useResizableTabPanelsContext();

  return (
    <img
      src={activeTabId === "projects" ? Icons.Action.AllProjects : Icons.Action.AllProjectsBW}
      width={20}
      height={20}
      alt={`View All ${getProjectLabel(true, true)}`}
    />
  );
};

const ProjectList: FC = () => {
  const [selectedProjectFileId, setSelectedProjectFileId] = useAtom(selectedProjectAtom);
  const currentFolder = useFilePickerStore((state) => state.currentFolder);
  const currentFolderProjectFileId = useMemo(() => getProjectFileIdFromFolder(currentFolder), [currentFolder]);

  useEffect(() => {
    // On mount (open), if a store has already been set (so already open)
    // This will align with the `initialSelectedKeys` set only if a store is present.
    if (
      (selectedProjectFileId == undefined && currentFolderProjectFileId) || // on mount
      (selectedProjectFileId && currentFolderProjectFileId && selectedProjectFileId !== currentFolderProjectFileId) // desynchronized
    ) {
      setSelectedProjectFileId(currentFolderProjectFileId);
    }
  }, [currentFolderProjectFileId]);

  return (
    <Card noBorder noPadding scrollable>
      <ProjectsListBox
        initialSelectedProjectId={currentFolder?.project?.id}
        defaultSelection={"private"}
        onSelectionChange={(project) => {
          setSelectedProjectFileId(project?.parentFileId);
        }}
        loadingFallback={<LoadingSpinner style={{ margin: "10px auto" }} />}
      />
    </Card>
  );
};

interface FilePickerFileListingProps {
  isSelectableFilter?: FileBrowserProps["isSelectableFilter"];
  selectableFileTypes?: FILE_BROWSE_TYPES[];
  singleSelection?: boolean;
}

const FilePickingPanel: FC<FilePickerFileListingProps> = (props) => {
  const { activeTabId } = useResizableTabPanelsContext();
  return activeTabId === "projects" ? (
    <FilePickerFileListing
      singleSelection={props.singleSelection}
      isSelectableFilter={props.isSelectableFilter}
      selectableFileTypes={props.selectableFileTypes}
    />
  ) : (
    <FileSearchResults singleSelection={props.singleSelection} isSelectableFilter={props.isSelectableFilter} />
  );
};

const FilePickerFileListing: FC<FilePickerFileListingProps> = (props) => {
  const [selectedProject] = useAtom(selectedProjectAtom);
  const currentFolder = useFilePickerStore((state) => state.currentFolder);
  const clearStore = useFilePickerStore((state) => state.clear);
  const currentFolderProjectFileId = useMemo(() => getProjectFileIdFromFolder(currentFolder), [currentFolder]);

  const initialFolderId = useMemo(() => {
    switch (selectedProject) {
      case currentFolderProjectFileId:
        return currentFolder?.id; // same project sub folder
      default:
        return selectedProject; // load the new project root
    }
  }, [selectedProject, currentFolderProjectFileId]);

  useEffect(() => {
    return clearStore;
  }, []);

  return initialFolderId ? (
    <FileBrowser
      initialFolderId={initialFolderId}
      useStore={useFilePickerStore}
      singleSelection={props.singleSelection}
      isSelectableFilter={props.isSelectableFilter}
      selectableFileTypes={props.selectableFileTypes}
    />
  ) : (
    <LoadingSpinner inCenter />
  );
};
